import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useSelector } from 'react-redux';
import t from 'react-translate';
import { AngularContext } from 'react-app';

import GradientAiIcon from 'lecture_pages/components/left-panel/novo-ai/components/shared/gradient-ai-icon';
import { NvTooltip } from 'shared/components/nv-tooltip';
import PusherService from 'shared/services/pusher-service';
import { getCurrentCourse, getFlatCourseAliases } from 'redux/selectors/course';
import { useAppDispatch } from 'redux/store';
import {
  getSingleSubmission,
  requestFeedback,
} from 'redux/actions/submissions';
import { AIFeedbackDataRequest, AIFeedbackErrorStatus } from 'redux/schemas/models/submissions';
import { InsightVisibility } from 'redux/schemas/models/exercise';
import aiAssignmentFeedbackStyles from './styles';
import AiFeedbackResponse from './ai-feedback-response';

const AI_FEEDBACK_EVENT = 'ai_feedback_received';
const AI_FEEDBACK_REQUESTED_EVENT = 'ai_feedback_requested';

const FEEDBACK_ERROR_CODES = [500, 502, 504, 404];

const isInternalErrorCode = (code: number | undefined): boolean => !!code && FEEDBACK_ERROR_CODES.includes(code);

const AiAssignmentFeedback: React.FC = () => {
  const [isGeneratingFeedback, setIsGeneratingFeedback] = useState<boolean>(false);
  const [hasPermissionError, setHasPermissionError] = useState<boolean>(false);
  const { injectServices } = React.useContext(AngularContext);
  const [ReportsManager] = injectServices(['ReportsManager']);

  const dispatch = useAppDispatch();
  const currentCourse = useSelector(getCurrentCourse);
  const courseAliases = useSelector(state => getFlatCourseAliases(state, currentCourse?.catalogId));
  const submissions = useSelector((state) => state?.models?.submissions);
  const { currentReport } = ReportsManager;
  const courseId = currentCourse?.id;
  const storeReport = submissions?.[currentReport?.id];
  const reportAiFeedback = storeReport?.aiFeedback || currentReport?.aiFeedback;
  const report = storeReport || currentReport;

  const feedbackDataRequest = (storeReport?.feedbackDataRequest || currentReport?.feedbackDataRequest) as AIFeedbackDataRequest;

  const responseCode = feedbackDataRequest?.feedbackProcessingStatus?.responseCode;
  const statusErrorMessage = feedbackDataRequest?.feedbackProcessingStatus?.errorMessage;

  const hasStatusError = feedbackDataRequest?.feedbackProcessingStatus?.status === 'error';
  const hasError = isInternalErrorCode(responseCode) || hasStatusError;

  const hasFeedbackDataRequest = !!feedbackDataRequest;
  const isGenerating = feedbackDataRequest?.completed === false || isGeneratingFeedback;

  const isLearnerView = report?.exercise?.insightVisibility === InsightVisibility.LEARNERS;

  const fetchByReportFeedback = useCallback(async () => {
    await dispatch(
      requestFeedback({
        catalogId: currentCourse.catalogId,
        reportId: currentReport.id,
      }),
    )
      .unwrap()
      .then(() => {
        setHasPermissionError(false);
        setIsGeneratingFeedback(true);
      })
      .catch(() => {
        setHasPermissionError(true);
      });
  }, [
    currentCourse.catalogId,
    currentReport.id,
    dispatch,
  ]);

  useEffect(() => {
    if (!hasFeedbackDataRequest || isInternalErrorCode(responseCode)) {
      fetchByReportFeedback();
    } else {
      setHasPermissionError(false);
    }
  }, [fetchByReportFeedback, hasFeedbackDataRequest, responseCode]);

  useEffect(() => {
    if (!courseId) {
      return undefined;
    }
    const courseChannel = PusherService.initializeCourseChannel(courseId);
    const handleAiFeedbackUpdate = async () => {
      await dispatch(
        getSingleSubmission({
          catalogId: currentCourse.catalogId,
          reportId: currentReport.id,
        }),
      )
        .unwrap()
        .then((updatedReport) => ReportsManager.currentReport.updateFromSubmissionData(updatedReport))
        .finally(() => {
          setIsGeneratingFeedback(false);
        });
    };

    if (courseChannel) {
      courseChannel.bind(AI_FEEDBACK_EVENT, handleAiFeedbackUpdate);
      courseChannel.bind(AI_FEEDBACK_REQUESTED_EVENT, handleAiFeedbackUpdate);
    }

    return () => {
      if (courseChannel) {
        courseChannel.unbind(AI_FEEDBACK_EVENT, handleAiFeedbackUpdate);
        courseChannel.unbind(AI_FEEDBACK_REQUESTED_EVENT, handleAiFeedbackUpdate);
      }
    };
  }, [courseId, currentCourse.catalogId, currentReport.id, dispatch, ReportsManager]);

  const getErrorMessage = (): string => {
    if (isInternalErrorCode(responseCode)) {
      return statusErrorMessage;
    }

    const errorMessages = {
      [AIFeedbackErrorStatus.SUBMISSION_TOO_SHORT]: t.SUBMISSIONS.AI_ASSIGNMENT.LACK_OF_CONTENT(),
      [AIFeedbackErrorStatus.CONTENT_IS_TOO_SHORT]: t.SUBMISSIONS.AI_ASSIGNMENT.LACK_OF_COURSE_CONTENT(),
    };
    return errorMessages[statusErrorMessage] || statusErrorMessage;
  };

  if (hasPermissionError || (!feedbackDataRequest && !isGenerating)) {
    return null;
  }

  return (
    <div id='ai-insights-section' css={aiAssignmentFeedbackStyles} data-qa='ai-insights-container'>
      <div className='ai-container'>
        {/* Header with AI Icon */}
        <div className='feedback-header'>
          <div className='feedback-title' data-qa='ai-insights-title'>
            <GradientAiIcon icon='gen-ai' size='small' className='ai-icon' />
            <span className='ai-label'>
              {isLearnerView ? t.SUBMISSIONS.AI_ASSIGNMENT.TITLE_INSIGHT() : t.SUBMISSIONS.AI_ASSIGNMENT.TITLE()}
            </span>
          </div>
          <NvTooltip
            text={isLearnerView
              ? t.SUBMISSIONS.AI_ASSIGNMENT.TAG_TOOLTIP_LEARNER(courseAliases)
              : t.SUBMISSIONS.AI_ASSIGNMENT.TAG_TOOLTIP(courseAliases)}
            placement='top'
          >
            <span className='visibility-tag' data-qa='ai-insights-visibility-tag'>
              {isLearnerView
                ? t.SUBMISSIONS.AI_ASSIGNMENT.TAG_LEARNER(courseAliases)
                : t.SUBMISSIONS.AI_ASSIGNMENT.TAG()}
            </span>
          </NvTooltip>
        </div>
        {/* Success Section */}
        {!hasError && (
          <AiFeedbackResponse
            generating={isGenerating}
            feedback={reportAiFeedback}
            feedbackDataRequest={feedbackDataRequest}
            isLearnerView={isLearnerView}
          />
        )}
        {/* Error Section */}
        {!isGenerating && hasError && (
          <div className='feedback-section'>
            {getErrorMessage()}
          </div>
        )}
      </div>
    </div>
  );
};

export default AiAssignmentFeedback;
